import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  EmailAddress,
  EmailAddressPayload,
  FaxNumber,
  FaxBookingRequest,
  BookFaxNumberData,
  NotificationData,
  NotificationListResponse,
  AddNotificationPayload,
} from './communication.model';
import { ReachabilityRef } from '../../core/models/reachability-ref.model';
import { environment } from '../../../environments/environment';
@Injectable({ providedIn: 'root' })
export class CommunicationService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.phoneServiceUrl}`;

  // --- Email CRUD ---

  getEmails(userAccountId: number): Observable<EmailAddress[]> {
    return this.http.get<EmailAddress[]>(`${this.base}/emails`, {
      params: { userAccountId: userAccountId.toString() },
    });
  }

  getDomains(): Observable<string[]> {
    return this.http.get<string[]>(`${this.base}/emails/domains`);
  }

  createEmail(payload: EmailAddressPayload): Observable<void> {
    return this.http.post<void>(`${this.base}/emails`, payload);
  }

  /** Trailing slash is critical for this endpoint */
  updateEmail(oldDomain: string, oldUsername: string, payload: EmailAddressPayload): Observable<void> {
    return this.http.put<void>(
      `${this.base}/emails/${encodeURIComponent(oldDomain)}/${encodeURIComponent(oldUsername)}/`,
      payload,
    );
  }

  /** Trailing slash is critical for this endpoint */
  deleteEmail(domain: string, username: string, userAccountId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/emails/${encodeURIComponent(domain)}/${encodeURIComponent(username)}/`,
      { params: { userAccountId: userAccountId.toString() } },
    );
  }

  // --- Email Notifications ---

  getEmailNotifications(domain: string, username: string): Observable<NotificationData[]> {
    return this.http.get<NotificationListResponse>(
      `${this.base}/emails/${encodeURIComponent(domain)}/${encodeURIComponent(username)}/notifications`,
    ).pipe(
      map(res => res._embedded?.notificationDataList ?? []),
    );
  }

  getEmailAddableReachabilities(domain: string, username: string): Observable<ReachabilityRef[]> {
    return this.http.get<ReachabilityRef[]>(
      `${this.base}/emails/${encodeURIComponent(domain)}/${encodeURIComponent(username)}/notifications/reachabilities/addable`,
    );
  }

  addEmailNotification(domain: string, username: string, payload: AddNotificationPayload): Observable<void> {
    return this.http.post<void>(
      `${this.base}/emails/${encodeURIComponent(domain)}/${encodeURIComponent(username)}/notifications`,
      payload,
    );
  }

  deleteEmailNotification(domain: string, username: string, notificationId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/emails/${encodeURIComponent(domain)}/${encodeURIComponent(username)}/notifications/${notificationId}`,
    );
  }

  // --- Fax CRUD ---

  getFaxNumbers(userAccountId: number): Observable<FaxNumber[]> {
    return this.http.get<FaxNumber[]>(`${this.base}/faxnumbers`, {
      params: { userAccountId: userAccountId.toString() },
    });
  }

  /**
   * Book a fax number for a user account. Multipart like the callcenter booking: JSON part
   * `bookFaxNumberData` + optional proof files — part names from phoneservice-api
   * `NumberController.bookFaxNumber` (OpenAPI has no body schema for this endpoint).
   * @ticket EB-3809
   */
  bookFaxNumber(userAccountId: number, employeeId: number, request: FaxBookingRequest): Observable<FaxNumber> {
    const bookFaxNumberData: BookFaxNumberData = {
      userAccountId,
      faxNumberBookingData: {
        countryPrefix: request.countryPrefix,
        cityPrefix: request.cityPrefix,
        cityName: request.cityName,
      },
      regulationInformation: request.regulationInformation,
      employeeId,
    };
    const fd = new FormData();
    fd.append('bookFaxNumberData',
      new Blob([JSON.stringify(bookFaxNumberData)], { type: 'application/json' }));
    if (request.addressProofFile) fd.append('proofOfAddressFile', request.addressProofFile);
    if (request.identityProofFile) fd.append('proofOfIdFile', request.identityProofFile);
    return this.http.post<FaxNumber>(`${this.base}/faxnumbers`, fd);
  }

  deleteFaxNumber(number: string, employeeId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/faxnumbers/${encodeURIComponent(number)}/useraccount`,
      { params: { employeeId: employeeId.toString() } },
    );
  }

  // --- Fax Notifications ---

  getFaxNotifications(number: string): Observable<NotificationData[]> {
    return this.http.get<NotificationListResponse>(
      `${this.base}/faxnumbers/${encodeURIComponent(number)}/notifications`,
    ).pipe(
      map(res => res._embedded?.notificationDataList ?? []),
    );
  }

  getFaxAddableReachabilities(number: string): Observable<ReachabilityRef[]> {
    return this.http.get<ReachabilityRef[]>(
      `${this.base}/faxnumbers/${encodeURIComponent(number)}/notifications/reachabilities/addable`,
    );
  }

  addFaxNotification(number: string, payload: AddNotificationPayload): Observable<void> {
    return this.http.post<void>(
      `${this.base}/faxnumbers/${encodeURIComponent(number)}/notifications`,
      payload,
    );
  }

  deleteFaxNotification(number: string, notificationId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/faxnumbers/${encodeURIComponent(number)}/notifications/${notificationId}`,
    );
  }
}
